import * as React from 'react';
import { Monitor, MapPin, Clock, X } from 'lucide-react';
import { Button } from './button';
import { Badge } from './badge';

export interface SessionRowData {
  id: string;
  ip_address: string | null;
  user_agent: string | null;
  device_info: string | null;
  created_at: string;
  expires_at: string;
  revoked: boolean;
}

export interface SessionRowProps {
  session: SessionRowData;
  revoking?: boolean;
  canRevoke?: boolean;
  onRevoke?: (sessionId: string) => void;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function shortUA(ua: string | null): string {
  if (!ua) return 'Dispositivo sconosciuto';
  const m = ua.match(/(Chrome|Firefox|Safari|Edge|Opera)\/[\d.]+/);
  const os = ua.match(/\(([^)]+)\)/);
  const browser = m?.[0] ?? 'Browser';
  const platform = os?.[1]?.split(';')[0]?.trim() ?? '';
  return platform ? `${browser} · ${platform}` : browser;
}

export const SessionRow: React.FC<SessionRowProps> = ({
  session,
  revoking = false,
  canRevoke = true,
  onRevoke,
}) => {
  return (
    <div className="flex items-start justify-between gap-3 p-3 rounded-lg border border-border dark:border-slate-700 hover:bg-muted/50 dark:hover:bg-white/5 transition-colors">
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <Monitor className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-foreground truncate">
            {session.device_info ?? shortUA(session.user_agent)}
          </div>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
            {session.ip_address && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {session.ip_address}
              </span>
            )}
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Creata: {formatDate(session.created_at)}
            </span>
            <span className="inline-flex items-center gap-1">
              Scade: {formatDate(session.expires_at)}
            </span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {session.revoked && (
          <Badge variant="destructive" className="text-[10px]">Revocata</Badge>
        )}
        {canRevoke && !session.revoked && onRevoke && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onRevoke(session.id)}
            disabled={revoking}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
            title="Revoca sessione"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};
